import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Plus,
  Search,
  UserPlus,
} from "lucide-react";
import {
  ApiError,
  createClubUser,
  createTeamInvite,
  getAdminTeams,
  getAdminUserDetail,
  getAdminUsers,
  grantAdminTeamMembership,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableHeaderRow,
  DataTablePagination,
  DataTableRow,
  DataTableScroll,
  DataTableShell,
} from "@/components/ui/DataTable";
import { unscopedKey } from "@/lib/queryClient";
import type { AdminTeamRow, AdminUser } from "@/lib/types";
import { AdminIconButton } from "@/components/console/AdminIconButton";

/**
 * People > Accounts — the CLUB people directory (admin-console-split PR 5).
 *
 * Embedded in `/admin/people`. Lists the people with standing in the HOST
 * club (`GET /api/users` is club-scoped on club hosts), with the hybrid
 * "Add person" dialog (invite by email or club-scoped direct create), team
 * assignment, and a detail drawer whose memberships are scrubbed to this
 * club. ACCOUNT-level actions (platform role, enable/disable, password,
 * delete) are super-admin-only — they live on the operator console's
 * Accounts page (`/super-admin/accounts`), because an account is a global
 * identity that can span clubs.
 *
 * G6 — the per-row "View teams" expander is a lightweight
 * collapsed alternative to opening the full detail drawer; it
 * fetches the SAME `/api/users/{id}` payload (so the drawer's
 * memberships and the expander's memberships are coherent) and
 * caches it for the page's lifetime.
 */

// Platform roles offered in the role FILTER. The legacy `uploader` token was
// retired in role-deprecation PR 4 — no account can carry it anymore.
const TEAM_ROLES = [
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "player", label: "Player" },
  { value: "guardian", label: "Guardian" },
];

const PAGE_SIZE_OPTIONS = [
  { value: "5", label: "5 / page" },
  { value: "10", label: "10 / page" },
  { value: "25", label: "25 / page" },
  { value: "50", label: "50 / page" },
];

/** Shared TanStack Query options for the user-detail payload. Both
 * the G3 detail drawer AND the G6 inline expander subscribe to the
 * same key + queryFn + staleTime — without this, the drawer
 * defaulted to `staleTime: 0` and refetched immediately after the
 * expander already had a fresh result, breaking the
 * "one-fetch-shared-by-both-surfaces" contract. Re-review P2 fix. */
function useAdminUserDetail(userId: string) {
  return useQuery({
    queryKey: unscopedKey("admin-user-detail", { userId }),
    queryFn: () => getAdminUserDetail(userId),
    staleTime: 5 * 60_000,
  });
}

export function AdminUsersPage({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({
    queryKey: unscopedKey("admin-users"),
    queryFn: getAdminUsers,
  });

  const users = usersQuery.data ?? [];
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [accountStatusFilter, setAccountStatusFilter] = useState("all");
  const [authMigrationFilter, setAuthMigrationFilter] = useState("all");
  const [pageSize, setPageSize] = useState(embedded ? "5" : "10");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<AdminUser | null>(null);
  const [assignTarget, setAssignTarget] = useState<AdminUser | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [secondaryFiltersOpen, setSecondaryFiltersOpen] = useState(false);

  const teamOptions = useMemo(() => {
    const teams = new Map<string, string>();
    for (const user of users) {
      for (const membership of user.team_memberships ?? []) {
        teams.set(membership.team_id, membership.team_name);
      }
    }
    return [...teams.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }, [users]);
  const availableRoles = useMemo(() => {
    const roles = new Set<string>();
    for (const user of users) {
      for (const role of user.roles ?? splitRoleString(user.role)) roles.add(role);
      for (const membership of user.team_memberships ?? []) {
        roles.add(membership.role);
      }
    }
    return [...roles].sort().map((role) => ({
      value: role,
      label: role.replaceAll("_", " "),
    }));
  }, [users]);

  const collectionControls = users.length > 5;
  const filtered = useMemo(() => {
    if (!collectionControls) return [...users].sort(compareAdminUsers);
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        const roles = u.roles ?? splitRoleString(u.role);
        const memberships = u.team_memberships ?? [];
        const matchesText =
          !q ||
          u.username.toLowerCase().includes(q) ||
          (u.display_name ?? "").toLowerCase().includes(q);
        const matchesTeam =
          teamFilter === "all" ||
          memberships.some((membership) => membership.team_id === teamFilter);
        const matchesRole =
          roleFilter === "all" ||
          roles.includes(roleFilter) ||
          memberships.some((membership) => membership.role === roleFilter);
        const matchesStatus =
          accountStatusFilter === "all" ||
          (accountStatusFilter === "active" ? u.enabled : !u.enabled);
        const kratosLinked = !!u.auth_migration?.kratos_linked;
        const matchesAuthMigration =
          authMigrationFilter === "all" ||
          (authMigrationFilter === "kratos_linked"
            ? kratosLinked
            : !kratosLinked);
        return (
          matchesText &&
          matchesTeam &&
          matchesRole &&
          matchesStatus &&
          matchesAuthMigration
        );
      })
      .sort(compareAdminUsers);
  }, [
    collectionControls,
    users,
    search,
    teamFilter,
    roleFilter,
    accountStatusFilter,
    authMigrationFilter,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    teamFilter,
    roleFilter,
    accountStatusFilter,
    authMigrationFilter,
    pageSize,
  ]);

  const pageSizeNumber = Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSizeNumber));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (!collectionControls) return filtered;
    const start = (safePage - 1) * pageSizeNumber;
    return filtered.slice(start, start + pageSizeNumber);
  }, [collectionControls, filtered, pageSizeNumber, safePage]);
  const firstVisible =
    filtered.length === 0 ? 0 : (safePage - 1) * pageSizeNumber + 1;
  const lastVisible = Math.min(filtered.length, safePage * pageSizeNumber);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-users") });
  const invalidateUserDetail = (userId: string) =>
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-user-detail", { userId }),
    });

  return (
    <div
      data-testid="admin-users"
      className={`flex flex-col gap-3 ${embedded ? "people-panel people-account-directory" : ""}`}
    >
      <header
        className={
          embedded
            ? "flex items-start justify-between gap-2"
            : "flex items-start justify-between flex-wrap gap-2"
        }
      >
        {embedded ? (
          <div className="min-w-0">
            <p className="people-panel-eyebrow">Club accounts</p>
            <h2 className="people-panel-title">Account directory</h2>
          </div>
        ) : (
          <div className="min-w-0">
            <h2 className="text-base font-display uppercase tracking-wider text-ink">
              Users
            </h2>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-[0.6rem] font-mono text-ink-muted">
            {usersQuery.isFetching
              ? "Refreshing…"
              : `${filtered.length} of ${users.length}`}
          </span>
          {embedded ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCreateOpen(true)}
              data-testid="admin-users-new"
            >
              Add person
            </Button>
          ) : (
            <AdminIconButton
              icon={Plus}
              label="Add user"
              size="md"
              tone="accent"
              onClick={() => setCreateOpen(true)}
              data-testid="admin-users-new"
            />
          )}
        </div>
      </header>

      {users.length > 5 ? (
        <section
          data-testid="admin-users-filters"
          aria-label="Account filters"
          className="people-directory-filters"
        >
          <label className="collection-search">
            <span>Quick search</span>
            <span className="relative block">
              <Search aria-hidden className="collection-search-icon" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, username or team"
                data-testid="admin-users-search"
              />
            </span>
          </label>
          <label className="collection-filter">
            <span>Team</span>
            <TkticSelect
              value={teamFilter}
              onChange={setTeamFilter}
              options={[{ value: "all", label: "All" }, ...teamOptions]}
              ariaLabel="Team filter"
              data-testid="admin-users-team-filter"
            />
          </label>
          <label className="collection-filter">
            <span>Role</span>
            <TkticSelect
              value={roleFilter}
              onChange={setRoleFilter}
              options={[{ value: "all", label: "All" }, ...availableRoles]}
              ariaLabel="Role filter"
              data-testid="admin-users-role-filter"
            />
          </label>
          {embedded ? (
            <button
              type="button"
              className="people-more-filters"
              aria-expanded={secondaryFiltersOpen}
              onClick={() => setSecondaryFiltersOpen((open) => !open)}
            >
              {secondaryFiltersOpen ? "Hide account filters" : "Account filters"}
            </button>
          ) : null}
          {!embedded || secondaryFiltersOpen ? (
            <div className="people-secondary-filters">
          <TkticSelect
            value={accountStatusFilter}
            onChange={setAccountStatusFilter}
            options={[
              { value: "all", label: "All status" },
              { value: "active", label: "Active" },
              { value: "disabled", label: "Disabled" },
            ]}
            ariaLabel="Account status filter"
            size="sm"
            data-testid="admin-users-status-filter"
          />
          <TkticSelect
            value={authMigrationFilter}
            onChange={setAuthMigrationFilter}
            options={[
              { value: "all", label: "All auth" },
              { value: "kratos_linked", label: "Secure sign-in linked" },
              { value: "needs_kratos", label: "Needs secure sign-in" },
            ]}
            ariaLabel="Auth migration filter"
            size="sm"
            data-testid="admin-users-auth-filter"
          />
          <TkticSelect
            value={pageSize}
            onChange={setPageSize}
            options={PAGE_SIZE_OPTIONS}
            ariaLabel="Rows per page"
            size="sm"
            data-testid="admin-users-page-size"
          />
            </div>
          ) : null}
        </section>
      ) : null}

      {usersQuery.error ? (
        <p
          data-testid="admin-users-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load users.
        </p>
      ) : null}

      {embedded ? (
        <div className="people-directory-list">
          {filtered.length === 0 ? (
            <p data-testid="admin-users-empty" className="people-empty-row">
              No accounts match these filters.
            </p>
          ) : (
            pageRows.map((user) => (
              <EmbeddedUserRow
                key={user.id}
                user={user}
                onOpenDetail={() => setDetailTarget(user)}
              />
            ))
          )}
        </div>
      ) : (
      <DataTableShell>
        {filtered.length === 0 ? (
          <p
            data-testid="admin-users-empty"
            className="text-xs font-mono text-ink-muted p-3"
          >
            No users.
          </p>
        ) : (
          <>
            {/* #211 — promoted from `md:block` to `lg:block` so the
              920px-min-width table doesn't horizontally overflow at the
              md breakpoint (768px). Tablet now gets the same card layout
              as mobile, which keeps row actions reachable without a
              horizontal scroll. */}
            <DataTableScroll className="hidden lg:block">
              <DataTable
                data-testid="admin-users-table"
                className="min-w-[920px]"
              >
                <DataTableHead>
                  <DataTableHeaderRow>
                    <DataTableHeadCell>Person</DataTableHeadCell>
                    <DataTableHeadCell>Role</DataTableHeadCell>
                    <DataTableHeadCell>Team status</DataTableHeadCell>
                    <DataTableHeadCell>Account</DataTableHeadCell>
                    <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                  </DataTableHeaderRow>
                </DataTableHead>
                <DataTableBody>
                  {pageRows.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      expanded={expandedUserId === u.id}
                      onToggleExpand={() =>
                        setExpandedUserId((id) => (id === u.id ? null : u.id))
                      }
                      onOpenDetail={() => setDetailTarget(u)}
                      onAssign={() => setAssignTarget(u)}
                    />
                  ))}
                </DataTableBody>
              </DataTable>
            </DataTableScroll>
            {/* #211 — matches the table breakpoint above; cards now
              cover all viewports < lg (1024px), eliminating the
              tablet-width horizontal scroll. */}
            <div className="divide-y divide-rule/60 lg:hidden">
              {pageRows.map((u) => (
                <UserCard
                  key={u.id}
                  user={u}
                  expanded={expandedUserId === u.id}
                  onToggleExpand={() =>
                    setExpandedUserId((id) => (id === u.id ? null : u.id))
                  }
                  onOpenDetail={() => setDetailTarget(u)}
                  onAssign={() => setAssignTarget(u)}
                />
              ))}
            </div>
          </>
        )}
      </DataTableShell>
      )}

      {collectionControls ? (
        <DataTablePagination
          first={firstVisible}
          last={lastVisible}
          total={filtered.length}
          page={safePage}
          totalPages={totalPages}
          onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          summaryTestId="admin-users-pagination-summary"
          previousTestId="admin-users-prev-page"
          indicatorTestId="admin-users-page-indicator"
          nextTestId="admin-users-next-page"
        />
      ) : null}

      {createOpen ? (
        <AddPersonModal
          onClose={() => setCreateOpen(false)}
          onDone={() => {
            setCreateOpen(false);
            void invalidate();
          }}
        />
      ) : null}

      {detailTarget ? (
        <UserDetailModal
          user={detailTarget}
          onClose={() => setDetailTarget(null)}
          onAssign={() => {
            setDetailTarget(null);
            setAssignTarget(detailTarget);
          }}
        />
      ) : null}

      {assignTarget ? (
        <AssignUserToTeamModal
          user={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => {
            const userId = assignTarget.id;
            setAssignTarget(null);
            setExpandedUserId(userId);
            void invalidate();
            void invalidateUserDetail(userId);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// G1 — list row + G6 inline expander
// ---------------------------------------------------------------------------

function EmbeddedUserRow({
  user,
  onOpenDetail,
}: {
  user: AdminUser;
  onOpenDetail: () => void;
}) {
  const roles = user.roles ?? splitRoleString(user.role);
  const memberships = user.team_memberships ?? [];
  return (
    <article
      data-testid="admin-users-row"
      data-user-id={user.id}
      data-enabled={user.enabled ? "true" : "false"}
      className="people-directory-row"
    >
      <div className="people-directory-row-main">
        <div className="min-w-0">
          <h3>{user.display_name || user.username}</h3>
          <p>{`@${user.username}`}</p>
        </div>
        <div className="sr-only">
          {roles.map((role) => (
            <RolePill key={role} role={role} />
          ))}
          {roles.includes("admin") ? (
            <span data-testid="admin-users-global-admin-pill">
              Super admin
            </span>
          ) : null}
          <span
            data-testid="admin-users-status-pill"
            data-enabled={user.enabled ? "true" : "false"}
          >
            {user.enabled ? "Active" : "Disabled"}
          </span>
          <span data-testid="admin-users-team-status">
            {memberships.length} team memberships
          </span>
        </div>
        <button
          type="button"
          className="people-manage-access"
          onClick={onOpenDetail}
          data-testid="admin-users-manage-access"
        >
          Manage access
        </button>
      </div>
    </article>
  );
}

function UserRow({
  user,
  expanded,
  onToggleExpand,
  onOpenDetail,
  onAssign,
}: {
  user: AdminUser;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenDetail: () => void;
  onAssign: () => void;
}) {
  const roles = user.roles ?? splitRoleString(user.role);
  return (
    <>
      <DataTableRow
        data-testid="admin-users-row"
        data-user-id={user.id}
        data-enabled={user.enabled ? "true" : "false"}
        className="align-top transition-colors hover:bg-elevated/60"
      >
        <DataTableCell className="align-top">
          <div className="text-ink truncate">
            {user.display_name || user.username}{" "}
            <span className="text-ink-muted">@{user.username}</span>
          </div>
          <div className="mt-0.5 font-mono text-[0.6rem] text-ink-muted">
            {user.id}
          </div>
        </DataTableCell>
        <DataTableCell className="align-top">
          <div className="flex flex-wrap items-center gap-1">
            {roles.map((r) => (
              <RolePill key={r} role={r} />
            ))}
            {/* Re-review P2: derive the Global-admin indicator from
             * the parsed `roles` array. `/api/users` returns
             * `_scrub_user(db_row)` which has `role` (comma-joined
             * string) but NOT a computed `is_global_admin` flag —
             * that lives only on `/api/me`. Reading `user.is_global_admin`
             * here meant the pill never rendered. Matches legacy
             * `renderUsersList` which derived the indicator from the
             * comma-joined role string. */}
            {roles.includes("admin") ? (
              <span
                data-testid="admin-users-global-admin-pill"
                className="rounded border border-accent/40 bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-accent"
              >
                Super admin
              </span>
            ) : null}
          </div>
        </DataTableCell>
        <DataTableCell className="align-top">
          <UserTeamStatus userId={user.id} />
        </DataTableCell>
        <DataTableCell className="align-top">
          <div className="flex flex-wrap items-center gap-1">
            <span
              data-testid="admin-users-status-pill"
              data-enabled={user.enabled ? "true" : "false"}
              className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${
                user.enabled
                  ? "border-good/40 text-good"
                  : "border-rule text-ink-muted"
              }`}
            >
              {user.enabled ? "Active" : "Disabled"}
            </span>
            <AuthMigrationPill user={user} />
          </div>
        </DataTableCell>
        <DataTableCell align="right" className="align-top">
          <div className="flex justify-end gap-1">
            <AdminIconButton
              icon={expanded ? ChevronUp : ChevronDown}
              label={expanded ? "Hide teams" : "View teams"}
              onClick={onToggleExpand}
              aria-expanded={expanded}
              data-testid="admin-users-expand"
            />
            <AdminIconButton
              icon={UserPlus}
              label="Assign to team"
              onClick={onAssign}
              data-testid="admin-users-assign"
            />
            <AdminIconButton
              icon={Eye}
              label="View user details"
              onClick={onOpenDetail}
              data-testid="admin-users-detail"
            />
          </div>
        </DataTableCell>
      </DataTableRow>
      {expanded ? (
        <DataTableRow interactive={false} className="bg-elevated">
          <DataTableCell colSpan={5} className="align-top">
            <UserMembershipsExpander userId={user.id} />
          </DataTableCell>
        </DataTableRow>
      ) : null}
    </>
  );
}

function UserCard({
  user,
  expanded,
  onToggleExpand,
  onOpenDetail,
  onAssign,
}: {
  user: AdminUser;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenDetail: () => void;
  onAssign: () => void;
}) {
  const roles = user.roles ?? splitRoleString(user.role);
  return (
    <article
      data-testid="admin-users-mobile-card"
      data-user-id={user.id}
      data-enabled={user.enabled ? "true" : "false"}
      className="flex flex-col gap-3 p-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm text-ink">
            {user.display_name || user.username}
          </h3>
          <p className="truncate text-xs text-ink-muted">@{user.username}</p>
          <p className="mt-1 truncate font-mono text-[0.6rem] text-ink-muted">
            {user.id}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <span
            data-testid="admin-users-status-pill"
            data-enabled={user.enabled ? "true" : "false"}
            className={`rounded border bg-surface px-2 py-1 text-[0.55rem] font-display uppercase tracking-wider ${
              user.enabled
                ? "border-good/40 text-good"
                : "border-rule text-ink-muted"
            }`}
          >
            {user.enabled ? "Active" : "Disabled"}
          </span>
          <AuthMigrationPill user={user} />
        </div>
      </header>

      <div className="grid gap-2">
        <div className="flex flex-col gap-1">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Role
          </span>
          <div className="flex flex-wrap items-center gap-1">
            {roles.map((r) => (
              <RolePill key={r} role={r} />
            ))}
            {roles.includes("admin") ? (
              <span
                data-testid="admin-users-global-admin-pill"
                className="rounded border border-accent/40 bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-accent"
              >
                Super admin
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Team status
          </span>
          <UserTeamStatus userId={user.id} />
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-1.5">
        <AdminIconButton
          icon={expanded ? ChevronUp : ChevronDown}
          label={expanded ? "Hide teams" : "View teams"}
          onClick={onToggleExpand}
          aria-expanded={expanded}
          size="md"
          data-testid="admin-users-mobile-expand"
        />
        <AdminIconButton
          icon={UserPlus}
          label="Assign to team"
          onClick={onAssign}
          size="md"
          data-testid="admin-users-mobile-assign"
        />
        <AdminIconButton
          icon={Eye}
          label="View user details"
          onClick={onOpenDetail}
          size="md"
          data-testid="admin-users-mobile-detail"
        />
      </div>

      {expanded ? (
        <div className="rounded border border-rule bg-elevated pt-2">
          <UserMembershipsExpander
            userId={user.id}
            testIdPrefix="admin-users-mobile"
          />
        </div>
      ) : null}
    </article>
  );
}

function UserTeamStatus({ userId }: { userId: string }) {
  const query = useAdminUserDetail(userId);

  if (query.isLoading) {
    return (
      <div
        data-testid="admin-users-team-status"
        className="rounded border border-rule bg-elevated px-2 py-1 text-[0.6rem] font-mono text-ink-muted"
      >
        Checking teams…
      </div>
    );
  }
  if (query.error) {
    return (
      <div
        data-testid="admin-users-team-status"
        className="rounded border border-destructive/40 bg-elevated px-2 py-1 text-[0.6rem] font-mono text-destructive"
      >
        Team status unavailable
      </div>
    );
  }
  const memberships = query.data?.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <div
        data-testid="admin-users-team-status"
        data-status="none"
        className="rounded border border-rule bg-elevated px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
      >
        No team
      </div>
    );
  }
  if (memberships.length === 1) {
    const membership = memberships[0];
    return (
      <div
        data-testid="admin-users-team-status"
        data-status="assigned"
        className="min-w-0 rounded border border-good/40 bg-elevated px-2 py-1"
      >
        <div className="truncate text-[0.65rem] text-ink">
          {membership.team_name}
        </div>
        <div className="truncate text-[0.55rem] font-display uppercase tracking-wider text-good">
          {membership.role.replace("_", " ")}
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="admin-users-team-status"
      data-status="multiple"
      className="min-w-0 rounded border border-accent/40 bg-elevated px-2 py-1"
    >
      <div className="text-[0.65rem] text-ink">{memberships.length} teams</div>
      <div className="truncate text-[0.55rem] font-mono text-ink-muted">
        {memberships
          .slice(0, 2)
          .map((m) => m.team_name)
          .join(", ")}
      </div>
    </div>
  );
}

function RolePill({ role }: { role: string }) {
  const tone =
    role === "admin"
      ? "border-accent text-accent"
      : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-users-role-pill"
      data-role={role}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {role}
    </span>
  );
}

function AuthMigrationPill({ user }: { user: AdminUser }) {
  const migration = user.auth_migration;
  const linked = !!migration?.kratos_linked;
  const verified = !!migration?.kratos_email_verified;
  const tone = linked
    ? verified
      ? "border-good/40 text-good"
      : "border-accent/40 text-accent"
    : "border-rule text-ink-muted";
  const label = linked
    ? verified
      ? "Secure sign-in"
      : "Sign-in unverified"
    : "Needs secure sign-in";
  return (
    <span
      data-testid="admin-users-auth-migration-pill"
      data-kratos-linked={linked ? "true" : "false"}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}

// G6 — Memberships expander. Reuses the `/api/users/{id}` endpoint
// (same payload as the detail drawer) so the two surfaces stay
// coherent. Cached by user id via the shared `useAdminUserDetail`
// hook — opening then re-opening doesn't refetch, AND opening the
// drawer after the expander reuses the same fresh cache (re-review
// P2 fix: both observers must use the same staleTime).
function UserMembershipsExpander({
  userId,
  testIdPrefix = "admin-users",
}: {
  userId: string;
  testIdPrefix?: "admin-users" | "admin-users-mobile";
}) {
  const query = useAdminUserDetail(userId);

  if (query.isLoading) {
    return (
      <div className="px-3 pb-2 text-[0.6rem] font-mono text-ink-muted">
        Loading teams…
      </div>
    );
  }
  if (query.error) {
    return (
      <div
        data-testid={`${testIdPrefix}-memberships-error`}
        className="px-3 pb-2 text-[0.6rem] font-mono text-destructive"
      >
        Failed to load memberships.
      </div>
    );
  }
  const memberships = query.data?.memberships ?? [];
  if (memberships.length === 0) {
    return (
      <div
        data-testid={`${testIdPrefix}-memberships-empty`}
        className="px-3 pb-2 text-[0.6rem] font-mono text-ink-muted"
      >
        No team memberships.
      </div>
    );
  }
  return (
    <ul
      data-testid={`${testIdPrefix}-memberships`}
      className="px-3 pb-2 flex flex-col gap-1"
    >
      {memberships.map((m) => (
        <li
          key={m.id}
          data-testid="admin-users-membership-row"
          data-team-id={m.team_id}
          className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1 text-[0.65rem]"
        >
          <span className="text-ink truncate">
            {m.team_name}{" "}
            <span className="font-mono text-ink-muted">· {m.team_slug}</span>
          </span>
          <span
            data-role={m.role}
            className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
          >
            {m.role.replace("_", " ")}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// G2 — Add user modal
// ---------------------------------------------------------------------------

/**
 * The club-host "Add person" dialog. Two paths, both requiring a team + a
 * real team role, both club-scoped by the server:
 *
 *   - Invite (default): email-first onboarding (POST /api/team/invites) —
 *     the person sets their own password and verifies their email.
 *   - Create directly: for people without email / on-the-spot setup
 *     (POST /api/club/users) — account + membership in one step, always a
 *     plain platform viewer (never a super admin).
 *
 * The operator/apex host keeps the trimmed CreateUserModal instead.
 */
function AddPersonModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    staleTime: 60_000,
  });
  const teams = useMemo(
    () =>
      [...(teamsQuery.data ?? [])]
        .filter((t) => t.slug !== "default-team")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [teamsQuery.data],
  );
  const teamOptions = teams.map((team: AdminTeamRow) => ({
    value: team.id,
    label: team.name,
  }));

  const [mode, setMode] = useState<"invite" | "create">("invite");
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState("coach");
  // Invite fields.
  const [email, setEmail] = useState("");
  // Direct-create fields.
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const inviteMut = useMutation({
    mutationFn: () =>
      createTeamInvite({ team_id: teamId, email: email.trim(), role }),
    onSuccess: () => {
      toast.success("Invite sent");
      onDone();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't send invite", { description: formatErr(err) });
    },
  });
  const createMut = useMutation({
    mutationFn: () =>
      createClubUser({
        username: username.trim(),
        password,
        display_name: displayName.trim() || undefined,
        team_id: teamId,
        role,
      }),
    onSuccess: () => {
      toast.success("Person created");
      onDone();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't create person", { description: formatErr(err) });
    },
  });
  const busy = inviteMut.isPending || createMut.isPending;

  const submit = () => {
    setError(null);
    if (!teamId) {
      setError("Pick a team.");
      return;
    }
    if (mode === "invite") {
      if (!email.trim()) {
        setError("Enter an email to invite.");
        return;
      }
      inviteMut.mutate();
      return;
    }
    if (username.trim().length < 2) {
      setError("Username must be at least 2 characters.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    createMut.mutate();
  };

  const tabClass = (active: boolean) =>
    `flex-1 rounded border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors ${
      active
        ? "border-accent bg-accent text-accent-ink"
        : "border-rule text-ink-muted hover:text-ink hover:bg-field"
    }`;

  return (
    <ConfirmDialog
      title="Add person"
      message={
        <div className="flex flex-col gap-3 text-xs">
          <div
            className="flex gap-1"
            role="tablist"
            aria-label="Add person mode"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "invite"}
              onClick={() => {
                setMode("invite");
                setError(null);
              }}
              className={tabClass(mode === "invite")}
              data-testid="admin-users-add-mode-invite"
            >
              Invite by email
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "create"}
              onClick={() => {
                setMode("create");
                setError(null);
              }}
              className={tabClass(mode === "create")}
              data-testid="admin-users-add-mode-create"
            >
              Create directly
            </button>
          </div>

          {teamsQuery.isLoading ? (
            <p className="font-mono text-ink-muted">Loading teams…</p>
          ) : teamsQuery.error ? (
            <p className="font-mono text-destructive">Failed to load teams.</p>
          ) : teams.length === 0 ? (
            <p className="font-mono text-ink-muted">
              Create a team first, then add people to it.
            </p>
          ) : (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Team
                </span>
                <TkticSelect
                  value={teamId}
                  onChange={setTeamId}
                  options={teamOptions}
                  placeholder="Pick a team"
                  ariaLabel="Team"
                  size="sm"
                  data-testid="admin-users-add-team"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Role
                </span>
                <TkticSelect
                  value={role}
                  onChange={setRole}
                  options={TEAM_ROLES}
                  ariaLabel="Role"
                  size="sm"
                  data-testid="admin-users-add-role"
                />
              </label>

              {mode === "invite" ? (
                <label className="flex flex-col gap-1">
                  <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                    Email
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="admin-users-add-email"
                    className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  <span className="text-[0.6rem] text-ink-muted">
                    They set their own password and verify their email.
                  </span>
                </label>
              ) : (
                <>
                  <label className="flex flex-col gap-1">
                    <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                      Username
                    </span>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      minLength={2}
                      maxLength={50}
                      data-testid="admin-users-add-username"
                      className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                      Display name
                    </span>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      maxLength={100}
                      data-testid="admin-users-add-display-name"
                      className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                      Password
                    </span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      data-testid="admin-users-add-password"
                      className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </label>
                </>
              )}
            </>
          )}
          {error ? (
            <p
              data-testid="admin-users-add-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={
        busy
          ? mode === "invite"
            ? "Sending…"
            : "Creating…"
          : mode === "invite"
            ? "Send invite"
            : "Create person"
      }
      onConfirm={submit}
      onClose={onClose}
      data-testid="admin-users-add-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// G4 — Edit user modal
// ---------------------------------------------------------------------------

function UserDetailModal({
  user,
  onClose,
  onAssign,
}: {
  user: AdminUser;
  onClose: () => void;
  onAssign: () => void;
}) {
  // Re-review P2 fix: share the same query options as the inline
  // G6 expander so opening the drawer after the expander reuses
  // the cached payload instead of triggering an immediate refetch.
  const query = useAdminUserDetail(user.id);

  return (
    <ConfirmDialog
      title={`Manage access · ${user.display_name || user.username}`}
      confirmLabel="Close"
      cancelLabel={null}
      wide
      message={
        query.isLoading ? (
          <p className="text-ink-muted font-mono text-xs">Loading…</p>
        ) : query.error ? (
          <p
            data-testid="admin-users-detail-error"
            className="text-destructive font-mono text-xs"
          >
            {formatErr(query.error)}
          </p>
        ) : query.data ? (
          <div className="admin-account-access-dialog">
            <section data-testid="admin-users-detail-profile">
              <header className="admin-account-access-heading">
                <div>
                  <span>Club account</span>
                  <h4>{query.data.user.display_name || query.data.user.username}</h4>
                  <p>@{query.data.user.username}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onAssign}
                  data-testid="admin-users-detail-assign"
                >
                  Assign to team
                </Button>
              </header>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 font-mono text-[0.65rem]">
                <dt className="text-ink-muted">Username</dt>
                <dd className="text-ink">@{query.data.user.username}</dd>
                <dt className="text-ink-muted">Display name</dt>
                <dd className="text-ink">
                  {query.data.user.display_name || "—"}
                </dd>
                <dt className="text-ink-muted">Roles</dt>
                <dd className="text-ink">{query.data.user.role}</dd>
                <dt className="text-ink-muted">Status</dt>
                <dd
                  className={
                    query.data.user.enabled ? "text-good" : "text-ink-muted"
                  }
                >
                  {query.data.user.enabled ? "Enabled" : "Disabled"}
                </dd>
                <dt className="text-ink-muted">Secure sign-in</dt>
                <dd className="text-ink">
                  {authMigrationText(query.data.user)}
                </dd>
                <dt className="text-ink-muted">Created</dt>
                <dd className="text-ink">
                  {query.data.user.created_at || "—"}
                </dd>
                <dt className="text-ink-muted">Last team</dt>
                <dd className="text-ink">
                  {query.data.user.last_team_id || "—"}
                </dd>
              </dl>
            </section>

            <section className="admin-account-memberships" data-testid="admin-users-detail-memberships">
              <header className="flex items-baseline justify-between mb-1">
                <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Team memberships ({query.data.memberships.length})
                </h4>
                <span className="text-[0.6rem] font-mono text-ink-muted">
                  Managed from the account row
                </span>
              </header>
              {query.data.memberships.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  No memberships.
                </p>
              ) : (
                <ul className="admin-account-membership-grid">
                  {query.data.memberships.map((m) => (
                    <li
                      key={m.id}
                      data-testid="admin-users-detail-membership-row"
                      className="admin-account-membership-card"
                    >
                      <div className="min-w-0 truncate">
                        <span className="text-ink">{m.team_name}</span>{" "}
                        <span className="text-ink-muted">· {m.team_slug}</span>{" "}
                        <span className="text-ink-muted">· {m.role}</span>
                      </div>
                      <span
                        data-team-id={m.team_id}
                        className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
                      >
                        Assigned
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="admin-users-detail-players">
              <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted mb-1">
                Linked players ({query.data.linked_players.length})
              </h4>
              {query.data.linked_players.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  Not linked to any players.
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {query.data.linked_players.map((p) => (
                    <li
                      key={`${p.player_id}-${p.relationship}`}
                      data-testid="admin-users-detail-player-row"
                      className="rounded border border-rule/60 bg-elevated px-2 py-1 font-mono text-[0.65rem]"
                    >
                      <span className="text-ink">{p.player_name}</span>{" "}
                      {p.jersey_number ? (
                        <span className="text-ink-muted">
                          #{p.jersey_number}
                        </span>
                      ) : null}{" "}
                      <span className="text-ink-muted">· {p.team_name}</span>{" "}
                      <span className="text-ink-muted">· {p.relationship}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null
      }
      onConfirm={onClose}
      onClose={onClose}
      data-testid="admin-users-detail-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// Row-level team assignment
// ---------------------------------------------------------------------------

function AssignUserToTeamModal({
  user,
  onClose,
  onAssigned,
}: {
  user: AdminUser;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const toast = useToast();
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    staleTime: 60_000,
  });
  const teams = useMemo(
    () =>
      [...(teamsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [teamsQuery.data],
  );
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState("coach");
  const [error, setError] = useState<string | null>(null);

  const selectedTeam = teams.find((team) => team.id === teamId);
  const teamOptions = teams.map((team: AdminTeamRow) => ({
    value: team.id,
    label: team.name,
  }));

  const mut = useMutation({
    mutationFn: () =>
      grantAdminTeamMembership(teamId, {
        user_id: user.id,
        role,
      }),
    onSuccess: () => {
      toast.success("Team assigned");
      onAssigned();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't assign team", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Assign to team"
      message={
        <div className="flex flex-col gap-3 text-xs">
          <p className="text-ink-muted">
            Add{" "}
            <strong className="text-ink">
              {user.display_name || user.username}
            </strong>{" "}
            to a team. The account row will show the assignment status after
            save.
          </p>
          {teamsQuery.isLoading ? (
            <p className="font-mono text-ink-muted">Loading teams…</p>
          ) : teamsQuery.error ? (
            <p className="font-mono text-destructive">Failed to load teams.</p>
          ) : teams.length === 0 ? (
            <p className="font-mono text-ink-muted">No teams yet.</p>
          ) : (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Team
                </span>
                <TkticSelect
                  value={teamId}
                  onChange={setTeamId}
                  options={teamOptions}
                  placeholder="Pick a team"
                  ariaLabel="Team"
                  size="sm"
                  data-testid="admin-users-assign-team"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Role
                </span>
                <TkticSelect
                  value={role}
                  onChange={setRole}
                  options={TEAM_ROLES}
                  ariaLabel="Role"
                  size="sm"
                  data-testid="admin-users-assign-role"
                />
              </label>
              {selectedTeam ? (
                <p className="rounded border border-rule bg-elevated px-2 py-1 font-mono text-[0.6rem] text-ink-muted">
                  {selectedTeam.slug} · {selectedTeam.game_format}
                </p>
              ) : null}
            </>
          )}
          {error ? (
            <p
              data-testid="admin-users-assign-error"
              className="font-mono text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Assigning…" : "Assign team"}
      onConfirm={() => {
        setError(null);
        if (!teamId) {
          setError("Pick a team.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-users-assign-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function splitRoleString(role: string | undefined): string[] {
  if (!role) return [];
  return role
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function compareAdminUsers(a: AdminUser, b: AdminUser): number {
  const roleDelta = roleRank(a) - roleRank(b);
  if (roleDelta !== 0) return roleDelta;
  const aLabel = (a.display_name || a.username).toLowerCase();
  const bLabel = (b.display_name || b.username).toLowerCase();
  const labelDelta = aLabel.localeCompare(bLabel);
  if (labelDelta !== 0) return labelDelta;
  return a.username.localeCompare(b.username);
}

function roleRank(user: AdminUser): number {
  const roles = user.roles ?? splitRoleString(user.role);
  if (roles.includes("admin")) return 0;
  return 1;
}

function authMigrationText(user: AdminUser): string {
  const migration = user.auth_migration;
  if (!migration?.kratos_linked) return "Needs secure sign-in link";
  return migration.kratos_email_verified
    ? "Linked and verified"
    : "Linked; email unverified";
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail)) {
      const messages = err.detail
        .map((entry) => {
          if (!entry || typeof entry !== "object") return null;
          const record = entry as Record<string, unknown>;
          const msg = typeof record.msg === "string" ? record.msg : null;
          const loc = Array.isArray(record.loc)
            ? record.loc
                .map((part) => String(part))
                .filter((part) => part !== "body")
                .join(".")
            : "";
          if (!msg) return null;
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter((msg): msg is string => !!msg);
      if (messages.length > 0) return messages.join("; ");
    }
    return `HTTP ${err.status}`;
  }
  return "Request failed";
}
